import { getCategoryImageSuffix } from '@susu/order-list'

import { getApiBaseUrl } from '../config/api.config'
import { getTokenFromUrl } from '../utils/auth'

import type { EnvironmentWithChina } from '../App'

export interface ItemsInCartApiItem {
  amount?: number
  CommercialDescription?: string
  commercialDescription?: string
  currency?: string
  fabric?: string
  identifier?: string
  imageUrl?: string | null
  ImageURL?: string | null
  image_url?: string | null
  marketingcloudpid?: string
  ProductID?: string
  productId?: string
  productID?: string
  quantity?: number
  variant_id?: string
  variation?: string
}

interface ProductImageApiItem {
  publicId?: string
  fileName?: string
  secureUrl?: string
}

interface StylePropertyApiItem {
  property?: string
  propertyValue?: string
}

interface StyleDetailsApiItem {
  styleCode?: string
  commercialDescription?: string
  properties?: StylePropertyApiItem[]
}

export class ItemsInCartApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public response?: unknown,
  ) {
    super(message)
    this.name = 'ItemsInCartApiError'
  }
}

function normalizeStyleCode(styleCode: string): string {
  return styleCode.trim().toLowerCase()
}

function getItemStyleCode(item: ItemsInCartApiItem): string | undefined {
  return item.identifier?.trim() || undefined
}

function getStyleFabric(style: StyleDetailsApiItem): string | undefined {
  const fabricProperty = style.properties?.find((property) =>
    property.property?.toLowerCase().includes('fabric'),
  )
  const property = fabricProperty ?? style.properties?.[0]
  return (
    property?.propertyValue?.trim() || property?.property?.trim() || undefined
  )
}

function getImageIdentifier(image: ProductImageApiItem): string {
  return image.publicId || image.fileName || ''
}

function optimizeImageUrl(url: string, width: number = 180): string {
  if (!url) return url

  try {
    const transformations = `w_${width},c_fill,f_auto,q_auto`
    const stripped = url.replace(
      /\/image\/upload\/(?!v\d)([^/]+\/)/,
      '/image/upload/',
    )

    return stripped.replace(
      '/image/upload/',
      `/image/upload/${transformations}/`,
    )
  } catch {
    return url
  }
}

function selectProductImageUrl(
  images: ProductImageApiItem[],
): string | undefined {
  if (images.length === 0) {
    return undefined
  }

  const suffix = getCategoryImageSuffix(images[0]?.publicId)
  const matchedImage = suffix
    ? images.find((image) => getImageIdentifier(image).endsWith(suffix))
    : undefined

  const imageUrl = matchedImage?.secureUrl || images[0]?.secureUrl

  return imageUrl ? optimizeImageUrl(imageUrl) : undefined
}

async function fetchItemImageUrl(
  baseUrl: string,
  styleCode: string,
  headers: Record<string, string>,
): Promise<string | undefined> {
  const response = await fetch(
    `${baseUrl}/public/productapi/v2/Image/FindImages?styleCode=${encodeURIComponent(
      styleCode,
    )}`,
    {
      method: 'GET',
      headers,
      mode: 'cors',
      credentials: 'omit',
    },
  )

  if (!response.ok) {
    return undefined
  }

  const data: unknown = await response.json()
  return Array.isArray(data)
    ? selectProductImageUrl(data as ProductImageApiItem[])
    : undefined
}

async function enrichItemsWithImages(
  items: ItemsInCartApiItem[],
  baseUrl: string,
  headers: Record<string, string>,
): Promise<ItemsInCartApiItem[]> {
  return Promise.all(
    items.map(async (item) => {
      const styleCode = item.identifier?.trim()

      if (!styleCode) {
        return item
      }

      try {
        const imageUrl = await fetchItemImageUrl(baseUrl, styleCode, headers)

        return imageUrl ? { ...item, imageUrl } : item
      } catch {
        return item
      }
    }),
  )
}

async function fetchStyleDetailsByCode(
  baseUrl: string,
  styleCodes: string[],
  headers: Record<string, string>,
): Promise<Map<string, StyleDetailsApiItem>> {
  if (styleCodes.length === 0) {
    return new Map()
  }

  const response = await fetch(
    `${baseUrl}/public/productapi/v2/Item/FindStyles`,
    {
      method: 'POST',
      headers,
      mode: 'cors',
      credentials: 'omit',
      body: JSON.stringify({
        styleCodes,
        pagination: {
          pageNumber: 0,
          pageSize: 0,
        },
        options: {
          shouldIncludeCommercialDescription: true,
          shouldIncludeAllProperties: true,
        },
      }),
    },
  )

  if (!response.ok) {
    return new Map()
  }

  const data: unknown = await response.json()
  if (!Array.isArray(data)) {
    return new Map()
  }

  return new Map(
    (data as StyleDetailsApiItem[])
      .filter((style) => style.styleCode)
      .map((style) => [normalizeStyleCode(style.styleCode!), style]),
  )
}

async function enrichItemsWithStyleDetails(
  items: ItemsInCartApiItem[],
  baseUrl: string,
  headers: Record<string, string>,
): Promise<ItemsInCartApiItem[]> {
  const styleCodes = Array.from(
    new Set(
      items
        .map(getItemStyleCode)
        .filter((styleCode): styleCode is string => Boolean(styleCode)),
    ),
  )

  try {
    const styleDetailsByCode = await fetchStyleDetailsByCode(
      baseUrl,
      styleCodes,
      headers,
    )

    return items.map((item) => {
      const styleCode = getItemStyleCode(item)
      const style = styleCode
        ? styleDetailsByCode.get(normalizeStyleCode(styleCode))
        : undefined

      if (!style) {
        return item
      }

      return {
        ...item,
        commercialDescription:
          style.commercialDescription?.trim() || item.commercialDescription,
        fabric: getStyleFabric(style) || item.fabric,
      }
    })
  } catch {
    return items
  }
}

function getItemsInCartRequestConfig(
  customerNumber: string,
  env: EnvironmentWithChina,
  authToken?: string,
  endpoint: 'items-in-cart' | 'recently-browsed' = 'items-in-cart',
): {
  baseUrl: string
  headers: Record<string, string>
  url: string
} {
  const normalizedCustomerNumber = customerNumber.trim()

  if (!normalizedCustomerNumber) {
    throw new ItemsInCartApiError('Missing customer number.')
  }

  const baseUrl = getApiBaseUrl(env)
  const url = `${baseUrl}/public/profileapi/${encodeURIComponent(
    normalizedCustomerNumber,
  )}/${endpoint}`
  const token = authToken || getTokenFromUrl()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  return { baseUrl, headers, url }
}

export async function fetchItemsInCartItems(
  customerNumber: string,
  env: EnvironmentWithChina,
  authToken?: string,
): Promise<ItemsInCartApiItem[]> {
  const { headers, url } = getItemsInCartRequestConfig(
    customerNumber,
    env,
    authToken,
  )

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
      mode: 'cors',
      credentials: 'omit',
    })

    if (!response.ok) {
      const errorBody: unknown = await response
        .json()
        .catch(() => response.text().catch(() => null))
      throw new ItemsInCartApiError(
        `Failed to fetch items in cart: ${response.statusText}`,
        response.status,
        errorBody,
      )
    }

    const data: unknown = await response.json()
    return Array.isArray(data) ? (data as ItemsInCartApiItem[]) : []
  } catch (error) {
    if (error instanceof ItemsInCartApiError) {
      throw error
    }

    throw new ItemsInCartApiError(
      `Network error while fetching items in cart: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`,
    )
  }
}

export async function enrichItemsInCartItems(
  items: ItemsInCartApiItem[],
  customerNumber: string,
  env: EnvironmentWithChina,
  authToken?: string,
): Promise<ItemsInCartApiItem[]> {
  if (items.length === 0) {
    return []
  }

  const { baseUrl, headers } = getItemsInCartRequestConfig(
    customerNumber,
    env,
    authToken,
  )

  const [itemsWithStyleDetails, itemsWithImages] = await Promise.all([
    enrichItemsWithStyleDetails(items, baseUrl, headers),
    enrichItemsWithImages(items, baseUrl, headers),
  ])

  return itemsWithStyleDetails.map((item, index) => ({
    ...item,
    imageUrl: itemsWithImages[index]?.imageUrl ?? item.imageUrl,
  }))
}

export async function fetchItemsInCart(
  customerNumber: string,
  env: EnvironmentWithChina,
  authToken?: string,
): Promise<ItemsInCartApiItem[]> {
  const items = await fetchItemsInCartItems(customerNumber, env, authToken)
  return enrichItemsInCartItems(items, customerNumber, env, authToken)
}

export async function fetchRecentlyBrowsedItems(
  customerNumber: string,
  env: EnvironmentWithChina,
  authToken?: string,
): Promise<ItemsInCartApiItem[]> {
  const { headers, url } = getItemsInCartRequestConfig(
    customerNumber,
    env,
    authToken,
    'recently-browsed',
  )

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
      mode: 'cors',
      credentials: 'omit',
    })

    if (!response.ok) {
      const errorBody: unknown = await response
        .json()
        .catch(() => response.text().catch(() => null))
      throw new ItemsInCartApiError(
        `Failed to fetch recently browsed items: ${response.statusText}`,
        response.status,
        errorBody,
      )
    }

    const data: unknown = await response.json()

    if (!Array.isArray(data)) {
      return []
    }

    return data
      .filter(
        (productCode): productCode is string => typeof productCode === 'string',
      )
      .map((productCode) => productCode.trim())
      .filter(Boolean)
      .map((productCode) => ({
        identifier: productCode,
        ProductID: productCode,
        productId: productCode,
      }))
  } catch (error) {
    if (error instanceof ItemsInCartApiError) {
      throw error
    }

    throw new ItemsInCartApiError(
      `Network error while fetching recently browsed items: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`,
    )
  }
}

export async function fetchRecentlyBrowsed(
  customerNumber: string,
  env: EnvironmentWithChina,
  authToken?: string,
): Promise<ItemsInCartApiItem[]> {
  const items = await fetchRecentlyBrowsedItems(customerNumber, env, authToken)
  return enrichItemsInCartItems(items, customerNumber, env, authToken)
}
